import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPill from "@/components/base/FilterPill";
import Lightbox from "@/components/feature/Lightbox";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { galleryList } from "@/mocks/gallery";
import { formatDotDate } from "@/utils/format";

const categories = ["全部", "写真", "活动", "宣传照"];

export default function GalleryPage() {
  useDocumentMeta({
    title: "张柏芝写真图集 - 高清图片与活动现场照",
    description:
      "收录张柏芝的写真、公开活动与宣传照高清图集，支持分类筛选与大图轮播浏览，按时间整理每一次镜头记录。",
    keywords: "张柏芝写真,张柏芝图片,张柏芝高清图,张柏芝活动照片,宣传照",
  });

  const [category, setCategory] = useState("全部");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const items = useMemo(
    () => (category === "全部" ? galleryList : galleryList.filter((item) => item.category === category)),
    [category],
  );

  const counts = {
    写真: galleryList.filter((item) => item.category === "写真").length,
    活动: galleryList.filter((item) => item.category === "活动").length,
    宣传照: galleryList.filter((item) => item.category === "宣传照").length,
  };

  return (
    <SiteLayout>
      <PageHero
        kicker="Gallery Archive"
        title="画像写真图集"
        description="棚拍写真、公开活动与影视宣传照的年度合集。图片按拍摄光线与时间整理，点击任意图片即可放大查看并左右翻页浏览。"
      >
        <Breadcrumb items={[{ label: "写真图集" }]} />
      </PageHero>

      <div className="w-full px-4 py-10 md:px-8 md:py-14 lg:px-12">
        <div className="flex flex-col gap-4 border-b border-background-200 pb-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((item) => (
              <FilterPill key={item} label={item} active={category === item} onClick={() => setCategory(item)} />
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-4 text-[11px] text-foreground-500">
            <span>写真 {counts.写真} 组</span>
            <span>活动 {counts.活动} 组</span>
            <span>宣传照 {counts.宣传照} 组</span>
            <span className="text-foreground-300">|</span>
            <span>当前显示 {items.length} 张</span>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 auto-rows-[160px] gap-3 md:auto-rows-[200px] md:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {items.map((item, index) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setLightboxIndex(index)}
              className={`group relative cursor-pointer overflow-hidden rounded-lg bg-background-200 ${
                item.ratio === "portrait" ? "row-span-2" : "row-span-1"
              }`}
            >
              <img
                src={item.image}
                alt={`${item.title} - 张柏芝${item.category}图片`}
                title={`${item.title} - 张柏芝写真图集`}
                className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
              />
              <span className="absolute left-2.5 top-2.5 rounded-full bg-background-50/90 px-2 py-0.5 text-[10px] text-foreground-800">
                {item.category}
              </span>
              <span className="absolute inset-x-0 bottom-0 translate-y-1 bg-gradient-to-t from-foreground-950/85 via-foreground-950/30 to-transparent px-3 pb-3 pt-10 text-left opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                <span className="block truncate font-heading text-xs text-background-50">{item.title}</span>
                <span className="mt-0.5 block text-[10px] text-background-300">{formatDotDate(item.date)}</span>
              </span>
              <span className="absolute right-2.5 top-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 opacity-0 transition-opacity group-hover:opacity-100">
                <i className="ri-zoom-in-line text-xs" aria-hidden="true"></i>
              </span>
            </button>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start gap-2 rounded-lg bg-background-100 p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs leading-6 text-foreground-600">
            图片为示范性内容，用于展示图集布局、大图查看与轮播交互效果。键盘方向键可切换图片，Esc 关闭大图。
          </p>
          <button
            type="button"
            onClick={() => setLightboxIndex(0)}
            className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-play-circle-line" aria-hidden="true"></i>
            从第一张开始轮播
          </button>
        </div>
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          items={items}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((current) => ((current ?? 0) - 1 + items.length) % items.length)}
          onNext={() => setLightboxIndex((current) => ((current ?? 0) + 1) % items.length)}
          onSelect={(target) => setLightboxIndex(target)}
        />
      )}
    </SiteLayout>
  );
}